import { SagaIterator } from "redux-saga";
import { call, put, takeEvery, select, SagaGenerator } from "typed-redux-saga";

import { Activity, ActivityActions } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { OverviewEngineActions } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions/internal/actions";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { isUser } from "../../utilities/user";

import {
	DASHBOARD_ASSIGNED_PROCESSES_OVERVIEW_MODEL,
	DASHBOARD_RECENT_CHANGES_OVERVIEW_MODEL,
	DASHBOARD_UPCOMING_DEADLINES_OVERVIEW_MODEL
} from "./constants";

const logger = LoggerFactory.getLogger("DashboardSaga");

export function* DashboardSaga(): SagaIterator<void> {
	yield* takeEvery(ActivityActions.push, function* (action) {
		if (action.payload.activity.descriptor.dashboard === "dashboard") {
			yield* call(createAssignedProcessesOverviewActivity, action.payload.activity);
			yield* call(createRecentChangesOverviewActivity, action.payload.activity);
			yield* call(createDeadlinesOverviewActivity, action.payload.activity);
		}
	});
}

function* createAssignedProcessesOverviewActivity(currentActivity: Activity): SagaGenerator<void> {
	const user = yield* select(UaaSelectors.user);
	if (!isUser(user)) {
		logger.error("Could not find current user. Cannot create overview for assignee processes.");
		return;
	}

	const assignedProcessesActivity = OverviewEngineActions.createActivity(
		{
			activityDescriptor: {
				model: DASHBOARD_ASSIGNED_PROCESSES_OVERVIEW_MODEL,
				...currentActivity.descriptor,
				dashboard: "assignedProcesses"
			},
			initiatingActivityId: currentActivity.id
		},
		{
			activeFilters: {
				"/root/processMetadata/task/assignee/id": {
					filterType: "String",
					criteria: { value: user.profile.preferred_username },
					nonRemovable: true
				},
				"/root/processMetadata/isFinished": {
					filterType: "Boolean",
					criteria: { value: false },
					nonRemovable: true
				}
			}
		}
	);

	yield* put(assignedProcessesActivity);
}

function* createRecentChangesOverviewActivity(currentActivity: Activity): SagaGenerator<void> {
	const recentChangesOverviewActivity = OverviewEngineActions.createActivity({
		activityDescriptor: {
			model: DASHBOARD_RECENT_CHANGES_OVERVIEW_MODEL,
			...currentActivity.descriptor,
			dashboard: "recentProcesses"
		},
		initiatingActivityId: currentActivity.id
	});
	yield* put(recentChangesOverviewActivity);
}

function* createDeadlinesOverviewActivity(currentActivity: Activity): SagaGenerator<void> {
	const deadlinesOverviewActivity = OverviewEngineActions.createActivity({
		activityDescriptor: {
			model: DASHBOARD_UPCOMING_DEADLINES_OVERVIEW_MODEL,
			...currentActivity.descriptor,
			dashboard: "upcomingDeadlines"
		},
		initiatingActivityId: currentActivity.id
	});
	yield* put(deadlinesOverviewActivity);
}
